import { API_URL } from "../config/Api_Url";

const STORY_CACHE_PREFIX = "story-cache-v1:";
const STORY_CACHE_TTL_MS = 30 * 60 * 1000;
const storyMemoryCache = new Map();
const storyRequestCache = new Map();

const requestJson = async (path) => {
  const response = await fetch(`${API_URL}${path}`, {
    credentials: "omit",
  });

  if (!response.ok) {
    throw new Error(`Request failed with status ${response.status}`);
  }

  return response.json();
};

const getStoryCacheKey = (id) => `${STORY_CACHE_PREFIX}${id}`;

const readStoryCacheEntry = (id) => {
  id = String(id);
  if (typeof window === "undefined") {
    return null;
  }

  if (storyMemoryCache.has(id)) {
    return storyMemoryCache.get(id);
  }

  try {
    const cachedStory = JSON.parse(
      localStorage.getItem(getStoryCacheKey(id)) || "null"
    );

    if (!cachedStory?.savedAt || !cachedStory?.data) {
      return null;
    }

    if (Date.now() - cachedStory.savedAt > STORY_CACHE_TTL_MS) {
      localStorage.removeItem(getStoryCacheKey(id));
      return null;
    }

    storyMemoryCache.set(id, cachedStory.data);

    return cachedStory.data;
  } catch (error) {
    return null;
  }
};

const writeStoryCacheEntry = (id, story) => {
  id = String(id);
  if (!story) {
    return story;
  }

  storyMemoryCache.set(id, story);

  if (typeof window === "undefined") {
    return story;
  }

  try {
    localStorage.setItem(
      getStoryCacheKey(id),
      JSON.stringify({
        data: story,
        savedAt: Date.now(),
      })
    );
  } catch (error) {
    // Ignore cache write failures and keep the network response.
  }

  return story;
};

const getWindowPrefetchedStory = (id) => {
  if (typeof window === "undefined") {
    return null;
  }

  if (
    window.__storyPrefetchId === String(id) &&
    window.__storyPrefetchData?.id != null
  ) {
    return window.__storyPrefetchData;
  }

  return null;
};

const fetchStory = (id) => {
  id = String(id);
  if (storyRequestCache.has(id)) {
    return storyRequestCache.get(id);
  }

  const storyRequest = requestJson(`/stories/${id}`)
    .then((story) => writeStoryCacheEntry(id, story))
    .finally(() => {
      storyRequestCache.delete(id);
    });

  storyRequestCache.set(id, storyRequest);

  return storyRequest;
};

const StoriesService = {
  cacheStory: function (story) {
    if (story?.id == null || !story.url) {
      return null;
    }

    return writeStoryCacheEntry(story.id, story);
  },

  getStories: async function () {
    return requestJson("/stories");
  },

  getStory: async function (id) {
    const cachedStory = readStoryCacheEntry(id) || getWindowPrefetchedStory(id);

    if (cachedStory) {
      writeStoryCacheEntry(id, cachedStory);
      return cachedStory;
    }

    if (
      typeof window !== "undefined" &&
      window.__storyPrefetchId === String(id) &&
      window.__storyPrefetch
    ) {
      const prefetchedStory = await window.__storyPrefetch;

      if (prefetchedStory) {
        return writeStoryCacheEntry(id, prefetchedStory);
      }
    }

    return fetchStory(id);
  },

  getCachedStory: function (id) {
    return readStoryCacheEntry(id) || getWindowPrefetchedStory(id);
  },

  prefetchStory: async function (id) {
    const cachedStory = this.getCachedStory(id);

    if (cachedStory) {
      return cachedStory;
    }

    return fetchStory(id);
  },
};

export default StoriesService;
